export const CHECKOUT_URL = "https://otoplasty-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/aRoz6ftG9qCnieXotWZ1ci/OTOPLASTY-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/qUqFxXok4BptDDKgeUeRz8/MICHAEL_CARTER_-_IMPLANTES.png",
};

export const pathwaySteps = [
  { title: "Assess", text: "Organize the anatomical and case-related information you are studying." },
  { title: "Understand", text: "Review the concepts and variables relevant to the educational case." },
  { title: "Plan", text: "Structure your review of the preoperative planning considerations." },
  { title: "Review", text: "Follow an organized educational overview of the procedure." },
  { title: "Reflect", text: "Identify questions, uncertainties, and topics that deserve further study." },
  { title: "Learn", text: "Document the lessons and organize what you want to review next." },
];

export const storyQuestions = [
  "What should I review first?",
  "Which anatomical features deserve closer attention?",
  "How should I organize the information available to me?",
  "What should I revisit before moving forward with my learning?",
];

export const problems = [
  {
    title: "You Have Multiple Resources but No Consistent Structure",
    text: "You've studied anatomy, reviewed surgical literature, and watched educational videos. But the information is scattered across different sources, and you spend time trying to connect the concepts yourself.",
  },
  {
    title: "You Want a More Organized Way to Review Otoplasty Cases",
    text: "Different anatomical characteristics, patient goals, and educational considerations can make each case feel like a separate learning challenge. You want a consistent framework for organizing what you observe and what you need to study.",
  },
  {
    title: "You Understand Individual Concepts but Want to Connect Them",
    text: "You may understand specific anatomical features or procedural concepts while still wanting a clearer way to organize the broader educational pathway.",
  },
  {
    title: "You Spend Too Much Time Reconstructing Your Notes",
    text: "One idea is in a textbook. Another is in a lecture. A third is somewhere in your personal notes. Instead of focusing on learning, you spend time searching for and reorganizing information.",
  },
  {
    title: "You Want to Study More Actively",
    text: "You don't want to consume educational content passively. You want structured questions, visual tools, and case-based exercises that help you identify what you understand and what you still need to review.",
  },
  {
    title: "You Want a Repeatable Learning Process",
    text: "You want to approach your educational review with a consistent structure rather than starting from scratch every time.",
  },
];

export const volumes = [
  {
    number: "I",
    color: "#1e3a6e",
    title: "The Otoplasty Assessment Framework",
    subtitle: "Organize your educational review before moving into planning concepts.",
    intro:
      "I created this volume to help you structure the information you review when studying otoplasty cases. Instead of jumping between anatomical observations, patient-related information, and scattered notes, you'll have a consistent framework for organizing your educational review.",
    listLabel: "You'll explore:",
    items: [
      "External ear anatomy review",
      "Anatomical terminology",
      "Case information organization",
      "Educational assessment categories",
      "Relevant observations to document",
      "Questions that deserve further investigation",
      "Patient-goal concepts for educational review",
      "Individual case documentation",
      "Knowledge gaps and learning priorities",
      "A structured case summary worksheet",
    ],
    note: "The objective is simple: organize the information you are studying before moving into the next stage of your learning.",
  },
  {
    number: "II",
    color: "#0f766e",
    title: "The Otoplasty Anatomy & Planning Map",
    subtitle: "See how the concepts connect instead of studying them in isolation.",
    intro:
      "I designed this volume to help you organize your understanding of external ear anatomy and the concepts commonly discussed in otoplasty education. You'll work through a visual framework that connects anatomical terminology, case characteristics, and educational planning considerations.",
    listLabel: "You'll explore:",
    items: [
      "External ear anatomy maps",
      "Anatomical terminology and definitions",
      "Visual concept diagrams",
      "Educational case variables",
      "Anatomical observation worksheets",
      "Concept comparison tables",
      "Planning-related questions for further study",
      "Visual knowledge maps",
      "Printable anatomy review sheets",
      "A structured learning summary",
    ],
    note: "The goal is to make your study more visual, organized, and easier to revisit.",
  },
  {
    number: "III",
    color: "#5b21b6",
    title: "The Otoplasty Clinical Pathway Blueprint",
    subtitle: "Review the educational pathway from preparation through postoperative follow-up.",
    intro:
      "This is the core of the system. I organized the educational content into a structured pathway so you can review how the different stages of otoplasty education connect. Instead of studying isolated concepts without an overarching structure, you'll have a visual overview that helps you organize the material.",
    listLabel: "You'll explore:",
    items: [
      "Educational preparation framework",
      "Preoperative review concepts",
      "Overview of procedural stages",
      "Technical terminology and concepts for further study",
      "Intraoperative educational considerations",
      "Postoperative review concepts",
      "Documentation and reflection prompts",
      "Stage-by-stage educational checklists",
      "Questions to discuss with qualified mentors",
      "A complete pathway overview",
    ],
    note: "This is an educational framework, not a universal operative protocol or a substitute for supervised surgical training, institutional procedures, or individualized clinical judgment.",
  },
  {
    number: "IV",
    color: "#a16207",
    title: "The Otoplasty Educational Case Atlas",
    subtitle: "Move beyond passive reading with structured case-based exercises.",
    intro:
      "I wanted this volume to help you actively engage with the information you study. You'll work through educational scenarios designed to help you organize observations, compare variables, formulate questions, and document your learning.",
    listLabel: "For each educational case, you'll:",
    items: [
      "Review the case information",
      "Identify the relevant educational variables",
      "Organize your observations",
      "Identify questions that need further investigation",
      "Review the relevant educational concepts",
      "Compare your observations with the educational commentary",
      "Record what you learned",
    ],
    note: "The scenarios are educational exercises, not individualized patient recommendations or validated clinical decision algorithms. Instead of simply consuming another lecture, you'll practice a more structured approach to studying cases.",
  },
  {
    number: "V",
    color: "#b91c1c",
    title: "The Otoplasty Complication & Case Review Atlas",
    subtitle: "Organize your study of complications and challenging educational scenarios.",
    intro:
      "I included this volume because understanding a procedure also means studying its limitations, potential complications, and the questions that arise when reviewing difficult cases. The objective isn't to memorize isolated warnings. It's to organize the information you need to investigate and understand the educational lessons associated with different scenarios.",
    listLabel: "You'll explore:",
    items: [
      "Educational complication scenarios",
      "Questions for reviewing the available information",
      "Factors discussed in relevant literature",
      "Documentation and case-review considerations",
      "Questions for further investigation",
      "Structured reflection worksheets",
      "Educational lessons and takeaways",
      "Literature review prompts",
      "Case comparison exercises",
      "A personal learning record",
    ],
    note: "All complication-related content should be interpreted in the context of current evidence, appropriate clinical supervision, and applicable professional guidance.",
  },
  {
    number: "VI",
    color: "#1d4ed8",
    title: "The Surgeon's Otoplasty Learning & Review System",
    subtitle: "Turn every study session into another opportunity to learn.",
    intro:
      "I wanted the system to help you beyond the first time you open it. This volume gives you reusable tools to organize your educational sessions, document questions, and track the subjects you want to revisit.",
    listLabel: "You'll receive templates for:",
    items: [
      "Educational case documentation",
      "Anatomy review",
      "Preoperative learning review",
      "Procedure overview review",
      "Case comparison",
      "Personal reflection",
      "Knowledge gap tracking",
      "Questions for future study",
      "Monthly learning reviews",
      "Progress documentation",
      "Professional development planning",
    ],
    note: "The idea is to build a structured record of your learning that you can revisit whenever you need it.",
  },
];

export const tools = [
  {
    title: "The 15-Minute Otoplasty Review Sheet",
    text: "A structured worksheet to help you organize an educational case review around a consistent set of questions.",
  },
  {
    title: "The Otoplasty Anatomy Review Checklist",
    text: "A printable checklist for organizing the anatomical concepts and terminology you want to review.",
  },
  {
    title: "The Clinical Pathway Map",
    text: "A visual overview of the educational pathway, connecting the main sections of the system.",
  },
  {
    title: "The Case Comparison Matrix",
    text: "A structured worksheet for comparing educational cases, documenting similarities and differences, and identifying questions for further study.",
  },
  {
    title: "The Visual Learning & Documentation Guide",
    text: "A guide to organizing educational diagrams, notes, references, and case-study materials.",
  },
];

export const bonuses = [
  {
    number: 1,
    image: "https://cdn.magicpatterns.com/uploads/fpDwpXBZtvr8WBv7M556T1/BONUS_1_OTOPLASTY-EN.png",
    title: "The 30 Otoplasty Educational Case Exercises",
    value: 47,
    description:
      "A collection of educational scenarios designed to help you repeatedly practice the Clinical Pathway framework. Each exercise follows a structured learning sequence:",
    sequence: ["Case", "Observe", "Analyze", "Review", "Reflect"],
    items: [] as string[],
    footer:
      "You'll find prompts to organize your observations, identify unanswered questions, and compare your learning across different scenarios. The exercises are designed for educational use and require appropriate professional interpretation.",
  },
  {
    number: 2,
    image: "https://cdn.magicpatterns.com/uploads/fm9h8Ugu1Re83PwXmkbUME/BONUS_2_OTOPLASTY-EN.png",
    title: "The 10-Minute Otoplasty Review™",
    value: 37,
    description:
      "Sometimes you want a shorter review framework to organize a focused study session. That's why I created this condensed worksheet. It guides you through a sequence of educational prompts:",
    sequence: ["Case", "Variables", "Questions", "Review", "Lesson"],
    items: [] as string[],
    footer:
      "Use it to organize a short review session without having to reopen the complete system every time. The suggested time is a study target, not a guarantee that every case can be adequately reviewed within ten minutes.",
  },
  {
    number: 3,
    image: "https://cdn.magicpatterns.com/uploads/wbt5BNPBkNtd7bvwZma95u/BONUS_3_OTOPLASTY-EN.png",
    title: "The Otoplasty Complication Review Workbook",
    value: 47,
    description:
      "A collection of educational prompts designed to help you structure the study of complications and challenging scenarios discussed in otoplasty literature. For each scenario, you'll organize:",
    sequence: [] as string[],
    items: [
      "What happened in the educational example?",
      "What information is available?",
      "Which factors deserve further investigation?",
      "What does the relevant literature discuss?",
      "What questions remain unanswered?",
      "What is the main educational lesson?",
    ],
    footer:
      "The purpose is to encourage structured learning and critical review, not to replace clinical assessment or prescribe management.",
  },
  {
    number: 4,
    image: "https://cdn.magicpatterns.com/uploads/c5B6DBqVXgncWoWk8AzSr4/BONUS_4_OTOPLASTY-EN.png",
    title: "The Surgeon's Otoplasty Review Cards",
    value: 37,
    description: "A collection of printable educational prompts for individual study or professional discussion. Examples include:",
    sequence: [] as string[],
    items: [
      "“Which anatomical concepts are most relevant to this educational case?”",
      "“What additional information would you want to review?”",
      "“Which observations deserve closer examination?”",
      "“What questions remain unanswered?”",
      "“Which concepts should you revisit in the literature?”",
      "“What did you learn from comparing these cases?”",
    ],
    footer: "One card. One focused question. One opportunity to organize your learning.",
  },
  {
    number: 5,
    image: "https://cdn.magicpatterns.com/uploads/hs7Bw9ftbWywqskvKotFpw/BONUS_5_OTOPLASTY-EN.png",
    title: "The 30-Day Otoplasty Learning Challenge™",
    value: 29,
    description:
      "A 30-day educational calendar designed to help you build a consistent study routine. Each day includes a suggested learning activity, such as:",
    sequence: [] as string[],
    items: [
      "Reviewing an anatomical concept",
      "Studying an educational case",
      "Comparing two scenarios",
      "Revisiting a difficult topic",
      "Reviewing a scientific reference",
      "Documenting a learning point",
      "Identifying a question for further investigation",
    ],
    footer:
      "One session. One learning objective. One documented takeaway. The challenge is designed to support consistent educational review without replacing formal professional development or supervised training.",
  },
];

export const offerIncludes = [
  "Instant digital access",
  "Lifetime access to the purchased materials",
  "No monthly subscription",
  "No recurring fees",
  "Seven-day money-back guarantee, subject to the purchase terms",
];

export const guaranteeItems = [
  "Open all six volumes",
  "Review the complete educational pathway",
  "Explore the anatomy maps",
  "Work through the educational case exercises",
  "Use the review checklists",
  "Test the comparison worksheets",
  "Explore all five bonuses",
];

export const beforeItems = [
  "Your educational resources are scattered across different sources.",
  "You spend time reconstructing your notes.",
  "You study individual concepts without a consistent framework.",
  "Your case-review notes follow different formats.",
  "You finish a study session without systematically documenting what you learned.",
  "You find it difficult to compare your observations across educational cases.",
];

export const afterItems = [
  "You have a repeatable framework for organizing your study.",
  "You can follow a visual educational pathway.",
  "You have standardized worksheets for reviewing cases.",
  "You can compare educational scenarios using a consistent structure.",
  "You have a system for documenting questions and learning points.",
  "You can revisit concepts using printable review tools.",
  "You can maintain a personal archive of your educational progress.",
];

export const testimonials = [
  {
    name: "Dr. Sarah Jensen",
    role: "Plastic Surgeon · Denver, USA",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/463442c3-cb10-43b0-b927-4d69bf1ab389.jpg",
    quote:
      "The six-step pathway finally gave my otoplasty study sessions a shape. I open the review sheet, work through Assess to Learn, and I actually finish with documented questions instead of scattered notes.",
  },
  {
    name: "Dr. Richard Hale",
    role: "Facial Plastic Surgeon · Manchester, UK",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/10f9ea38-8621-40ca-99ee-1d99d744440f.jpg",
    quote:
      "The anatomy maps and printable checklists are what I come back to most. Clean, visual, and easy to revisit before a teaching session with residents.",
  },
  {
    name: "Dr. Arjun Mehta",
    role: "Plastic Surgery Resident · Toronto, Canada",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/ce3877e7-b99d-4560-adc2-b0050cab4056.jpg",
    quote:
      "The case exercises made my studying active. Comparing my observations with the educational commentary showed me exactly which topics I needed to revisit in the literature.",
  },
  {
    name: "Dr. Lucía Romero",
    role: "ENT & Facial Plastic Surgeon · Austin, USA",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/5cbdfd6f-b332-4b30-9074-faddbf8646d3.jpg",
    quote:
      "For $27 the organization of the six volumes alone was worth it. The Case Comparison Matrix is now part of how I review every educational case.",
  },
];

export const authorPillars = [
  "Visual pathways",
  "Anatomical learning maps",
  "Educational cases",
  "Review worksheets",
  "Structured questions",
  "Reflection",
  "Ongoing learning",
];

export const faqs = [
  {
    q: "How Will I Receive Otoplasty Clinical Pathway System™?",
    a: "After completing your purchase, you'll receive instructions for accessing the digital materials. The volumes and printable resources will be provided in the formats specified at checkout.",
  },
  {
    q: "Who Is This System For?",
    a: "It is intended for qualified medical professionals studying otoplasty, particularly those who want a more organized way to review educational concepts, cases, and procedural information. It is not intended for patients seeking instructions to perform or manage a surgical procedure themselves.",
  },
  {
    q: "Is This a Surgical Training Course?",
    a: "No. It is a digital educational resource designed to organize professional learning. It does not replace formal surgical education, hands-on training, supervision, mentorship, or clinical experience.",
  },
  {
    q: "Does It Provide a Universal Step-by-Step Surgical Protocol?",
    a: "No. The system provides an educational pathway for reviewing concepts and organizing learning. It does not prescribe a universal operative sequence or replace individualized clinical judgment, institutional protocols, or current professional guidance.",
  },
  {
    q: "How Long Does Each Review Take?",
    a: "The system includes a suggested 15-minute educational review framework, along with shorter review tools. Actual study time depends on the complexity of the material and the user's educational objectives.",
  },
  {
    q: "Can I Print the Materials?",
    a: "Yes, the designated printable worksheets and review tools can be printed for personal educational use.",
  },
  {
    q: "Is This a One-Time Payment?",
    a: "Yes. The listed launch offer is a one-time payment of $27 USD, with no recurring subscription fees.",
  },
  {
    q: "How Long Will I Have Access?",
    a: "The offer includes lifetime access to the purchased digital materials, subject to the applicable purchase terms.",
  },
  {
    q: "What Happens If I Don't Like It?",
    a: "You can request a refund within seven days of purchase under the stated guarantee terms. Please review the purchase terms for the process and any applicable conditions.",
  },
  {
    q: "Is the Content Suitable for Every Country?",
    a: "The material is intended for professional education. Clinical practice requirements, institutional protocols, and professional regulations vary by jurisdiction. Users remain responsible for following the requirements applicable to their practice.",
  },
];

export const recentBuyers = [
  { name: "Dr. Alejandro Ruiz", country: "Mexico", flag: "🇲🇽" },
  { name: "Dr. Valentina Gómez", country: "Colombia", flag: "🇨🇴" },
  { name: "Dr. Martín Fernández", country: "Argentina", flag: "🇦🇷" },
  { name: "Dr. Camila Rojas", country: "Chile", flag: "🇨🇱" },
  { name: "Dr. Diego Herrera", country: "Spain", flag: "🇪🇸" },
  { name: "Dr. Sofía Castillo", country: "Peru", flag: "🇵🇪" },
  { name: "Dr. Andrés Morales", country: "Ecuador", flag: "🇪🇨" },
  { name: "Dr. Lucía Navarro", country: "Uruguay", flag: "🇺🇾" },
  { name: "Dr. Javier Torres", country: "Costa Rica", flag: "🇨🇷" },
  { name: "Dr. Isabella Vargas", country: "Dominican Republic", flag: "🇩🇴" },
  { name: "Dr. Mateo Sánchez", country: "Guatemala", flag: "🇬🇹" },
  { name: "Dr. Gabriela Méndez", country: "Panama", flag: "🇵🇦" },
  { name: "Dr. Carlos Ortega", country: "Puerto Rico", flag: "🇵🇷" },
  { name: "Dr. Daniela Paredes", country: "Bolivia", flag: "🇧🇴" },
  { name: "Dr. Ricardo Benítez", country: "Paraguay", flag: "🇵🇾" },
];
